import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MagnifyingGlass, Plus, Ticket, NotePencil, X, Eye, QrCode } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError, todayStr, CHANNELS } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import CheckinCouponModal from "../components/CheckinCouponModal";
import usePendingCount from "../hooks/usePendingCount";
import PaxInput from "../components/PaxInput";

const FILTERS = [
  { key: "", label: "Tutte" },
  { key: "da_confermare", label: "Da confermare" },
  { key: "agenda", label: "Agenda" },
  { key: "confermata", label: "Confermate" },
  { key: "in", label: "IN" },
  { key: "out", label: "OUT" },
  { key: "no_show", label: "No-show" },
  { key: "cancellata", label: "Cancellate" },
  { key: "lista_attesa", label: "Lista d'attesa" },
  { key: "rifiutata", label: "Rifiutate" },
];

const fmtAgendaDate = (d) => {
  const s = new Date(d + "T12:00:00").toLocaleDateString("it-IT",
    { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export function ReservationForm({ initial, rooms, onSubmit, onClose, title, submitLabel, testidPrefix }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [existingCust, setExistingCust] = useState(null);
  const [suggest, setSuggest] = useState([]);
  const [suggestField, setSuggestField] = useState(null); // "nome" | "telefono"
  const pickedRef = useRef(false);
  const [etichette, setEtichette] = useState({ coupons: [], events: [], promos: [], webforms: [] });
  useEffect(() => {
    api.get("/tags").then((r) => setAllTags(r.data)).catch(() => {});
    api.get("/coupons").then((r) => setEtichette((p) => ({ ...p, coupons: (r.data || []).filter((c) => c.active) }))).catch(() => {});
    api.get("/events").then((r) => setEtichette((p) => ({ ...p, events: (r.data || []).filter((e) => e.stato === "attiva") }))).catch(() => {});
    api.get("/promotions").then((r) => setEtichette((p) => ({ ...p, promos: (r.data || []).filter((x) => x.stato === "attiva") }))).catch(() => {});
    api.get("/web-forms").then((r) => setEtichette((p) => ({ ...p,
      webforms: [...new Map((r.data || []).filter((f) => f.active && (f.etichetta || "").trim())
        .map((f) => [f.etichetta.trim(), { id: f.id, name: f.etichetta.trim() }])).values()] }))).catch(() => {});
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Nessun aggancio/precompilazione automatica: il cliente esistente viene
  // agganciato SOLO se l'operatore lo clicca nella tendina suggerimenti.

  // autocomplete cliente CRM: dopo 3 caratteri su nome o telefono mostra la tendina suggerimenti
  useEffect(() => {
    if (!("customer" in form) || !suggestField) { setSuggest([]); return; }
    if (pickedRef.current) { pickedRef.current = false; setSuggest([]); return; }
    const raw = suggestField === "nome" ? (form.customer.first_name || "") : (form.customer.phone || "");
    const q = suggestField === "telefono" ? raw.replace(/\s/g, "") : raw.trim();
    if (q.length < 3) { setSuggest([]); return; }
    const t = setTimeout(() => {
      api.get("/customers", { params: { q } }).then((r) => setSuggest((r.data || []).slice(0, 6)))
        .catch(() => setSuggest([]));  // errore o zero risultati: la tendina sparisce
    }, 300);
    return () => clearTimeout(t);
  }, [form.customer?.first_name, form.customer?.phone, suggestField]);

  const pickCustomer = (c) => {
    pickedRef.current = true;
    setExistingCust(c);
    setSuggest([]);
    setSuggestField(null);
    setForm((f) => ({
      ...f,
      customer: {
        ...f.customer,
        customer_id: c.id,  // aggancio esplicito scelto dall'operatore
        first_name: c.first_name || "",
        last_name: c.last_name || "",
        phone: c.phone || "",
        email: c.email || "",
        tags: (c.tags || []).length ? c.tags : (f.customer.tags || []),
        allergie: c.allergie || f.customer.allergie || "",
      },
    }));
  };
  const allTables = rooms.flatMap((r) => r.tables.map((t) => ({ ...t, room_name: r.name })));
  // filtro live: la tendina resta visibile solo se i suggerimenti corrispondono
  // ancora al testo digitato; se il contatto non esiste sparisce e il sistema
  // si limita a registrare esattamente quello che scrive l'operatore
  const qLive = suggestField === "telefono"
    ? (form.customer?.phone || "").replace(/\D/g, "")
    : (form.customer?.first_name || "").trim().toLowerCase();
  const minLen = suggestField === "telefono" ? 4 : 3;
  const suggestLive = !suggestField || qLive.length < minLen ? [] : suggest.filter((c) => {
    if (suggestField === "telefono") return (c.phone || "").replace(/\D/g, "").includes(qLive);
    return `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase().includes(qLive);
  });
  const selectedCustomerTags = ("customer" in form && form.customer.tags) || [];
  const toggleCustomerTag = (name) => setForm((f) => {
    if (!("customer" in f)) return f;
    const cur = f.customer.tags || [];
    return {
      ...f,
      customer: {
        ...f.customer,
        tags: cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name],
      },
    };
  });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit(form);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full h-11 px-3 rounded-md border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 bg-white";
  const labelCls = "block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-lg max-h-[92vh] overflow-y-auto rounded-t-xl"
        onClick={(e) => e.stopPropagation()} data-testid={`${testidPrefix}-dialog`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 sticky top-0 bg-white">
          <h2 className="font-display font-bold text-lg text-zinc-900">{title}</h2>
          <button onClick={onClose} className="p-2 text-zinc-500" data-testid={`${testidPrefix}-close`}><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {"status_choice" in form && (
            <div>
              <label className={labelCls}>Stato prenotazione *</label>
              <div className="flex gap-2 flex-wrap">
                {[["confermata", "Accetta prenotazione", "bg-emerald-600 text-white border-emerald-600"],
                  ["lista_attesa", "Metti in lista d'attesa", "bg-sky-600 text-white border-sky-600"],
                  ["rifiutata", "Rifiuta prenotazione", "bg-rose-600 text-white border-rose-600"]].map(([k, lbl, activeCls]) => (
                  <button type="button" key={k} onClick={() => set("status_choice", k)} data-testid={`${testidPrefix}-stato-${k}`}
                    className={`h-11 px-4 rounded-md border text-xs font-bold transition ${form.status_choice === k ? activeCls : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
          )}
          {"customer" in form ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Nome *</label>
                <input required value={form.customer.first_name} data-testid={`${testidPrefix}-nome`}
                  onChange={(e) => { setSuggestField("nome"); set("customer", { ...form.customer, first_name: e.target.value, customer_id: null }); }}
                  autoComplete="off" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Cognome</label>
                <input value={form.customer.last_name} data-testid={`${testidPrefix}-cognome`}
                  onChange={(e) => set("customer", { ...form.customer, last_name: e.target.value, customer_id: null })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Telefono</label>
                <input value={form.customer.phone} data-testid={`${testidPrefix}-telefono`}
                  onChange={(e) => { setSuggestField("telefono"); set("customer", { ...form.customer, phone: e.target.value, customer_id: null }); }}
                  autoComplete="off" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input type="email" value={form.customer.email} data-testid={`${testidPrefix}-email`}
                  onChange={(e) => set("customer", { ...form.customer, email: e.target.value, customer_id: null })} className={inputCls} />
              </div>
              {suggestLive.length > 0 && (
                <div className="col-span-2 -mt-1 rounded-md border border-zinc-200 bg-white shadow-lg divide-y divide-zinc-100 max-h-48 overflow-y-auto"
                  data-testid={`${testidPrefix}-suggest`}>
                  {suggestLive.map((c) => (
                    <button type="button" key={c.id} onClick={() => pickCustomer(c)}
                      data-testid={`${testidPrefix}-suggest-${c.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-zinc-50 transition flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-zinc-900">{c.first_name} {c.last_name}</span>
                      <span className="text-xs text-zinc-500 shrink-0">{c.phone}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="col-span-2">
                <label className={labelCls}>Allergie / intolleranze</label>
                <input value={form.customer.allergie || ""} data-testid={`${testidPrefix}-allergie`}
                  onChange={(e) => set("customer", { ...form.customer, allergie: e.target.value })}
                  placeholder="Es. glutine, lattosio, frutta secca..." className={inputCls} />
              </div>
              {existingCust && (
                <div className="col-span-2 flex items-center gap-2 flex-wrap" data-testid={`${testidPrefix}-cliente-esistente`}>
                  <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded">
                    Cliente esistente: dati precompilati
                  </span>
                  {existingCust.no_shows > 0 && (
                    <span className="text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 rounded"
                      data-testid={`${testidPrefix}-noshow-badge`}>
                      Attenzione: {existingCust.no_shows} no-show precedenti
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : null}
          {"customer" in form && (
            <div>
              <label className={labelCls}>Tag cliente</label>
              <div className="flex gap-1.5 flex-wrap items-center" data-testid={`${testidPrefix}-tags-selected`}>
                {selectedCustomerTags.length === 0 && (
                  <span className="text-xs text-zinc-400">Nessun tag selezionato</span>
                )}
                {selectedCustomerTags.map((name) => (
                  <span key={name} data-testid={`${testidPrefix}-tag-selected-${name}`}
                    className="inline-flex items-center gap-1 h-8 pl-3 pr-1 rounded-full bg-zinc-900 text-white text-xs font-semibold">
                    {name}
                    <button type="button" aria-label={`Rimuovi tag ${name}`} data-testid={`${testidPrefix}-tag-remove-${name}`}
                      onClick={() => toggleCustomerTag(name)}
                      className="w-6 h-6 rounded-full hover:bg-zinc-700 flex items-center justify-center transition">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <button type="button" onClick={() => setTagsOpen((o) => !o)} data-testid={`${testidPrefix}-tags-toggle`}
                className="mt-2 h-9 px-3 rounded-md border border-zinc-300 bg-white text-xs font-semibold text-zinc-600 hover:border-zinc-500 transition">
                {tagsOpen ? "Chiudi elenco tag" : `Scegli tag${allTags.length ? ` (${allTags.length})` : ""}`}
              </button>
              {tagsOpen && (
                <div className="mt-2 max-h-44 overflow-y-auto rounded-md border border-zinc-200 p-2 flex gap-1.5 flex-wrap" data-testid={`${testidPrefix}-tags`}>
                  {allTags.length === 0 && (
                    <span className="text-xs text-zinc-400">Nessun tag creato: gestiscili da Clienti → Tags</span>
                  )}
                  {allTags.map((t) => {
                    const sel = selectedCustomerTags.includes(t.name);
                    return (
                      <button type="button" key={t.id || t.name} data-testid={`${testidPrefix}-tag-${t.name}`}
                        onClick={() => toggleCustomerTag(t.name)}
                        className={`h-8 px-3 rounded-full text-xs font-semibold border transition ${
                          sel ? "bg-zinc-900 text-white border-zinc-900"
                              : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"}`}>
                        {t.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Data *</label>
              <input type="date" required value={form.date} data-testid={`${testidPrefix}-data`}
                onChange={(e) => set("date", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Ora *</label>
              <input type="time" required value={form.time} data-testid={`${testidPrefix}-ora`}
                onChange={(e) => set("time", e.target.value)} className={inputCls} />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className={labelCls}>Persone</label>
              <PaxInput value={form.party_size} onChange={(v) => set("party_size", v)} min={1} max={99}
                testid={`${testidPrefix}-persone`} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Tavolo</label>
              <select value={form.table_id || ""} data-testid={`${testidPrefix}-tavolo`}
                onChange={(e) => {
                  const t = allTables.find((x) => x.id === e.target.value);
                  setForm((f) => ({ ...f, table_id: e.target.value || null, room_id: t ? t.room_id : f.room_id }));
                }} className={inputCls}>
                <option value="">Non assegnato</option>
                {allTables.map((t) => (
                  <option key={t.id} value={t.id}>{t.room_name} · Tav. {t.number} ({t.seats} coperti)</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Canale</label>
              <select value={form.channel || "telefono"} data-testid={`${testidPrefix}-canale`}
                onChange={(e) => set("channel", e.target.value)} className={inputCls}>
                {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Etichetta prenotazione</label>
            <select value={form.etichetta || ""} data-testid={`${testidPrefix}-etichetta`}
              onChange={(e) => set("etichetta", e.target.value)} className={inputCls}>
              <option value="">— Nessuna —</option>
              {form.etichetta && ![
                ...etichette.coupons.map((c) => c.name),
                ...etichette.events.map((e) => e.name),
                ...etichette.promos.map((p) => p.name),
                ...etichette.webforms.map((w) => w.name),
              ].includes(form.etichetta) && (
                <option value={form.etichetta}>{form.etichetta} (attuale)</option>
              )}
              {etichette.coupons.length > 0 && (
                <optgroup label="Coupon">
                  {etichette.coupons.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </optgroup>
              )}
              {etichette.events.length > 0 && (
                <optgroup label="Eventi">
                  {etichette.events.map((e) => <option key={e.id} value={e.name}>{e.name}</option>)}
                </optgroup>
              )}
              {etichette.promos.length > 0 && (
                <optgroup label="Promo">
                  {etichette.promos.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                </optgroup>
              )}
              {etichette.webforms.length > 0 && (
                <optgroup label="Moduli web">
                  {etichette.webforms.map((w) => <option key={w.id} value={w.name}>{w.name}</option>)}
                </optgroup>
              )}
            </select>
            <p className="text-[11px] text-zinc-400 mt-1">Scegli tra coupon, eventi, promo e moduli web attivi: resta visibile sulla riga della prenotazione.</p>
          </div>
          {"notify_customer" in form && (
            <div className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 transition ${form.notify_customer ? "border-emerald-200 bg-emerald-50" : "border-zinc-200 bg-zinc-50"}`}>
              <div>
                <p className="text-sm font-semibold text-zinc-900">Notifica email al cliente</p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {form.notify_customer
                    ? "Il cliente riceve l'email di conferma e le comunicazioni su questa prenotazione"
                    : "Il cliente non riceve nulla: né conferma, né promemoria, né cancellazione"}
                </p>
              </div>
              <button type="button" role="switch" aria-checked={form.notify_customer}
                onClick={() => set("notify_customer", !form.notify_customer)}
                data-testid={`${testidPrefix}-notify-toggle`}
                className={`shrink-0 w-11 h-6 rounded-full transition relative ${form.notify_customer ? "bg-emerald-600" : "bg-zinc-300"}`}>
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.notify_customer ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </div>
          )}
          <div>
            <label className={labelCls}>Note del cliente</label>
            <textarea rows={2} value={form.customer_notes || ""} data-testid={`${testidPrefix}-note-cliente`}
              onChange={(e) => set("customer_notes", e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
          </div>
          <div>
            <label className={labelCls}>Note interne (staff)</label>
            <textarea rows={2} value={form.internal_notes || ""} data-testid={`${testidPrefix}-note-interne`}
              onChange={(e) => set("internal_notes", e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-zinc-900" />
          </div>
          <button type="submit" disabled={saving} data-testid={`${testidPrefix}-submit`}
            className="w-full h-12 rounded-md bg-zinc-900 text-white font-semibold text-sm hover:bg-zinc-800 active:scale-[0.98] transition disabled:opacity-50">
            {saving ? "Salvataggio..." : ({"confermata": "Salva e conferma", "lista_attesa": "Salva e metti in lista d'attesa", "rifiutata": "Salva e rifiuta"}[form.status_choice] || submitLabel)}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Prenotazioni() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [date, setDate] = useState(searchParams.get("date") || todayStr());
  const [status, setStatus] = useState(searchParams.get("stato") || "");
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [pending, setPending] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [review, setReview] = useState(null);
  const [checkinCouponRes, setCheckinCouponRes] = useState(null);
  const [payReq, setPayReq] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [loading, setLoading] = useState(true);
  const reqId = useRef(0);
  const { pendingCount, refreshPending } = usePendingCount();

  const load = useCallback(() => {
    const id = ++reqId.current;
    setLoading(true);
    const params = new URLSearchParams();
    // "Da confermare" mostra tutte le richieste in sospeso, a prescindere dalla data:
    // una richiesta pubblica per domani deve comparire subito, anche se la vista è su oggi.
    // "Agenda": tutte le confermate da oggi in poi, senza filtro data (raggruppate sotto).
    if (date && status !== "da_confermare" && status !== "agenda") params.set("date", date);
    if (status === "agenda") params.set("status", "confermata");
    else if (status) params.set("status", status);
    if (q) params.set("q", q);
    api.get(`/reservations?${params}`).then((r) => {
      if (id === reqId.current) setList(r.data);  // ignora risposte di richieste superate
      refreshPending();  // badge "Da confermare" sempre sincronizzato con la lista
    }).catch(() => {})
      .finally(() => { if (id === reqId.current) setLoading(false); });
    // Richieste online in sospeso: sempre pinnate in cima, a prescindere dalla data visualizzata
    if (status !== "da_confermare") {
      api.get("/reservations?status=da_confermare").then((r) => {
        if (id === reqId.current) setPending(r.data);
      }).catch(() => {});
    } else {
      setPending([]);
    }
  }, [date, status, q, refreshPending]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {}); }, []);

  const quickAction = async (e, res, newStatus) => {
    e.stopPropagation();
    try {
      await api.post(`/reservations/${res.id}/status`, { status: newStatus });
      toast.success(`Stato aggiornato: ${newStatus === "in" ? "Arrivato (IN)" : newStatus.toUpperCase()}`);
      load();
      // check-in: se il cliente ha coupon attivi non agganciati, proponili allo staff
      if (newStatus === "in" && !res.coupon_id && res.customer_id) setCheckinCouponRes(res);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const sendPaymentRequest = async () => {
    const amount = parseFloat(String(payAmount).replace(",", "."));
    if (!amount || amount <= 0) { toast.error("Inserisci un importo valido"); return; }
    try {
      await api.post(`/reservations/${payReq.id}/payment-request`, { amount });
      toast.success(`Richiesta di pagamento inviata (€ ${amount.toFixed(2)})`);
      setPayReq(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const notifyAction = async (e, res, kind, label) => {
    e.stopPropagation();
    try {
      const r = await api.post(`/reservations/${res.id}/notify`, { kind });
      toast.success(r.data.skipped ? `${label} già inviata` : `${label} inviata al cliente`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const createReservation = async (form) => {
    await api.post("/reservations", form);
    toast.success("Prenotazione creata");
    setShowNew(false);
    load();
  };

  const reviewSubmit = async (form) => {
    await api.patch(`/reservations/${review.id}`, {
      date: form.date, time: form.time, party_size: form.party_size,
      room_id: form.room_id, table_id: form.table_id || null,
      customer_notes: form.customer_notes, internal_notes: form.internal_notes,
      channel: form.channel, customer: form.customer,
    });
    await api.post(`/reservations/${review.id}/status`, { status: form.status_choice });
    toast.success(form.status_choice === "confermata" ? "Prenotazione confermata" : "Prenotazione aggiornata");
    setReview(null);
    load();
  };

  const Row = ({ r, showDate }) => (
    <div onClick={() => navigate(`/prenotazioni/${r.id}`)} data-testid={`reservation-row-${r.id}`}
      className="bg-white border border-zinc-200 rounded-lg p-3 sm:p-4 cursor-pointer hover:border-zinc-400 transition-colors">
      <div className="flex items-center gap-3">
        <div className="w-14 shrink-0">
          <div className="font-display font-bold text-xl text-zinc-900">{r.time}</div>
          {showDate && (
            <div className="text-[11px] font-semibold text-cyan-700" data-testid={`card-date-${r.id}`}>
              {new Date(r.date + "T12:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-zinc-900 truncate">{r.customer_name}</span>
            <span className="text-xs text-zinc-500">{r.party_size} pers.</span>
            {r.table_number && <span className="text-xs font-medium text-zinc-600 bg-zinc-100 px-1.5 py-0.5 rounded">Tav. {r.table_number}</span>}
            {r.coupon_name && (
              <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded flex items-center gap-1">
                <Ticket size={11} weight="bold" /> {r.coupon_name}
              </span>
            )}
            {r.etichetta && (
              <span className="text-xs font-medium text-cyan-700 bg-cyan-50 border border-cyan-200 px-1.5 py-0.5 rounded"
                data-testid={`card-etichetta-${r.id}`}>{r.etichetta}</span>
            )}
            {r.customer_no_shows > 0 && (
              <span data-testid={`card-noshow-${r.id}`}
                className="text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">
                {r.customer_no_shows} no-show
              </span>
            )}
            {r.customer_allergie && (
              <span title={r.customer_allergie} data-testid={`card-allergie-${r.id}`}
                className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                Allergie
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-zinc-500">
            <span className="capitalize">{r.channel}</span>
            {r.customer_notes && <span className="flex items-center gap-1 text-amber-700"><NotePencil size={12} /> Note cliente</span>}
            {r.customer_phone && <span className="hidden sm:inline">{r.customer_phone}</span>}
          </div>
        </div>
        <StatusBadge status={r.status} />
      </div>
      {(r.status === "da_confermare" || r.status === "confermata" || r.status === "in" || r.status === "lista_attesa") && (
        <div className="flex gap-2 mt-3 flex-wrap" onClick={(e) => e.stopPropagation()}>
          {r.status === "da_confermare" && (
            <button onClick={(e) => { e.stopPropagation(); setReview(r); }} data-testid={`btn-rivedi-${r.id}`}
              className="h-10 px-3 rounded-md bg-white border border-zinc-400 text-zinc-700 text-xs font-bold hover:bg-zinc-50 active:scale-95 transition flex items-center gap-1.5">
              <Eye size={14} weight="bold" /> RIVEDI
            </button>
          )}
          {(r.status === "da_confermare" || r.status === "lista_attesa") && (
            <button onClick={(e) => quickAction(e, r, "confermata")} data-testid={`btn-conferma-${r.id}`}
              className="h-10 px-4 rounded-md bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 active:scale-95 transition">
              CONFERMA
            </button>
          )}
          {r.status === "da_confermare" && (
            <>
              <button onClick={(e) => quickAction(e, r, "lista_attesa")} data-testid={`btn-lista-attesa-${r.id}`}
                className="h-10 px-3 rounded-md bg-sky-600 text-white text-xs font-bold hover:bg-sky-700 active:scale-95 transition">
                LISTA D'ATTESA
              </button>
              <button onClick={(e) => quickAction(e, r, "rifiutata")} data-testid={`btn-rifiuta-${r.id}`}
                className="h-10 px-3 rounded-md bg-white border border-rose-300 text-rose-700 text-xs font-bold hover:bg-rose-50 active:scale-95 transition">
                RIFIUTA
              </button>
            </>
          )}
          {(r.status === "da_confermare" || r.status === "confermata") && (
            <>
              <button onClick={(e) => quickAction(e, r, "in")} data-testid={`btn-arrivato-${r.id}`}
                className="h-10 px-5 rounded-md bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 active:scale-95 transition">
                ARRIVATO
              </button>
              <button onClick={(e) => quickAction(e, r, "no_show")} data-testid={`btn-noshow-${r.id}`}
                className="h-10 px-3 rounded-md bg-white border border-rose-300 text-rose-700 text-xs font-bold hover:bg-rose-50 active:scale-95 transition">
                NO-SHOW
              </button>
              <button onClick={(e) => quickAction(e, r, "cancellata")} data-testid={`btn-cancella-${r.id}`}
                className="h-10 px-3 rounded-md bg-white border border-zinc-300 text-zinc-600 text-xs font-bold hover:bg-zinc-50 active:scale-95 transition">
                CANCELLA
              </button>
            </>
          )}
          {r.status === "lista_attesa" && (
            <button onClick={(e) => quickAction(e, r, "cancellata")} data-testid={`btn-chiudi-lista-${r.id}`}
              className="h-10 px-3 rounded-md bg-white border border-zinc-300 text-zinc-600 text-xs font-bold hover:bg-zinc-50 active:scale-95 transition">
              CHIUDI LISTA D'ATTESA
            </button>
          )}
          {r.status === "confermata" && (
            <>
              <button onClick={(e) => notifyAction(e, r, "tavolo_pronto", "Tavolo pronto")} data-testid={`btn-tavolo-pronto-${r.id}`}
                className="h-10 px-3 rounded-md bg-white border border-cyan-300 text-cyan-700 text-xs font-bold hover:bg-cyan-50 active:scale-95 transition">
                TAVOLO PRONTO
              </button>
              <button onClick={(e) => notifyAction(e, r, "carta_garanzia", "Richiesta carta a garanzia")} data-testid={`btn-carta-${r.id}`}
                className="h-10 px-3 rounded-md bg-white border border-zinc-300 text-zinc-600 text-xs font-bold hover:bg-zinc-50 active:scale-95 transition">
                CARTA A GARANZIA
              </button>
              <button onClick={(e) => { e.stopPropagation(); setPayReq(r); setPayAmount(""); }} data-testid={`btn-pagamento-${r.id}`}
                className="h-10 px-3 rounded-md bg-white border border-zinc-300 text-zinc-600 text-xs font-bold hover:bg-zinc-50 active:scale-95 transition">
                RICHIEDI PAGAMENTO
              </button>
            </>
          )}
          {r.status === "in" && (
            <button onClick={(e) => quickAction(e, r, "out")} data-testid={`btn-out-${r.id}`}
              className="h-10 px-5 rounded-md bg-zinc-900 text-white text-xs font-bold hover:bg-zinc-800 active:scale-95 transition">
              OUT — LIBERA TAVOLO
            </button>
          )}
        </div>
      )}
    </div>
  );

  // Agenda: confermate da oggi in poi, raggruppate per data (solo giorni con prenotazioni)
  const agendaGroups = status === "agenda" ? (() => {
    const today = todayStr();
    const byDate = {};
    for (const r of list) {
      if (r.date >= today) (byDate[r.date] = byDate[r.date] || []).push(r);
    }
    return Object.keys(byDate).sort().map((d) => ({
      date: d, items: byDate[d],
      coperti: byDate[d].reduce((s, x) => s + (x.party_size || 0), 0),
    }));
  })() : [];

  return (
    <div className="space-y-4" data-testid="prenotazioni-page">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Prenotazioni</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/scanner")} data-testid="btn-scanner-qr"
            className="h-11 px-4 rounded-md border border-zinc-300 bg-white text-sm font-semibold flex items-center gap-2 text-zinc-700 hover:bg-zinc-50 active:scale-95 transition">
            <QrCode size={16} weight="bold" /> <span className="hidden sm:inline">Scanner QR</span>
          </button>
          <button onClick={() => setShowNew(true)} data-testid="btn-nuova-prenotazione"
            className="h-11 px-4 rounded-md bg-zinc-900 text-white text-sm font-semibold flex items-center gap-2 hover:bg-zinc-800 active:scale-95 transition">
            <Plus size={16} weight="bold" /> Nuova
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        {status !== "agenda" && (
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="filter-data"
            className="h-11 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900" />
        )}
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome o telefono..." data-testid="filter-search"
            className="w-full h-11 pl-9 pr-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900" />
        </div>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="filter-stati">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setStatus(f.key)} data-testid={`filter-${f.key || "tutte"}`}
            className={`shrink-0 h-9 px-3 rounded-full text-xs font-semibold border transition-colors inline-flex items-center ${
              status === f.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>
            {f.label}
            {f.key === "da_confermare" && pendingCount > 0 && (
              <span data-testid="badge-da-confermare"
                className="ml-1.5 min-w-5 h-5 px-1.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold inline-flex items-center justify-center">
                {pendingCount > 99 ? "99+" : pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {pending.length > 0 && status !== "da_confermare" && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2" data-testid="pending-section">
          <button onClick={() => setStatus("da_confermare")} data-testid="pending-banner"
            className="w-full flex items-center justify-between gap-2">
            <span className="text-xs font-bold text-amber-800 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              {pending.length} {pending.length === 1 ? "richiesta online da confermare" : "richieste online da confermare"}
            </span>
            <span className="text-[11px] font-semibold text-amber-700 underline">Vedi solo queste</span>
          </button>
          {pending.map((r) => <Row key={r.id} r={r} showDate />)}
        </div>
      )}

      <div className="space-y-2" data-testid="prenotazioni-list">
        {loading && <div className="text-sm text-zinc-400 py-8 text-center">Caricamento...</div>}
        {!loading && status === "agenda" && agendaGroups.length === 0 && pending.length === 0 && (
          <div className="bg-white border border-zinc-200 rounded-lg py-10 text-center text-sm text-zinc-400" data-testid="agenda-empty">
            Nessuna prenotazione confermata in programma
          </div>
        )}
        {!loading && status !== "agenda" && list.length === 0 && pending.length === 0 && (
          <div className="bg-white border border-zinc-200 rounded-lg py-10 text-center text-sm text-zinc-400" data-testid="prenotazioni-empty">
            Nessuna prenotazione per i filtri selezionati
          </div>
        )}
        {status === "agenda"
          ? agendaGroups.map((g) => (
            <div key={g.date} className="space-y-2" data-testid={`agenda-day-${g.date}`}>
              <div className="flex items-center gap-2 pt-2">
                <span className="text-xs font-bold uppercase tracking-wider text-cyan-700" data-testid={`agenda-date-${g.date}`}>
                  {fmtAgendaDate(g.date)}
                </span>
                <span className="text-[11px] font-medium text-zinc-400">
                  {g.items.length} prenotaz. · {g.coperti} coperti
                </span>
                <div className="flex-1 h-px bg-zinc-200" />
              </div>
              {g.items.map((r) => <Row key={r.id} r={r} />)}
            </div>
          ))
          : list.map((r) => <Row key={r.id} r={r} showDate={status === "da_confermare"} />)}
      </div>

      {payReq && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3"
          onClick={() => setPayReq(null)} data-testid="payment-request-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="font-display text-lg font-bold text-zinc-900">Richiedi pagamento</h3>
              <p className="text-sm text-zinc-500 mt-0.5">
                Caparra per {payReq.customer_name}: il cliente riceve un'email con il link di pagamento sicuro (Stripe).
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-zinc-400">€</span>
              <input type="number" min="1" step="0.5" value={payAmount} autoFocus
                onChange={(e) => setPayAmount(e.target.value)} data-testid="payment-amount-input"
                placeholder="20.00"
                className="flex-1 h-12 px-3 rounded-md border border-zinc-300 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-zinc-900" />
            </div>
            <div className="flex gap-2">
              <button onClick={sendPaymentRequest} data-testid="payment-request-send"
                className="flex-1 h-11 rounded-md bg-zinc-900 text-white text-sm font-bold hover:bg-zinc-800 active:scale-95 transition">
                INVIA RICHIESTA
              </button>
              <button onClick={() => setPayReq(null)} data-testid="payment-request-cancel"
                className="h-11 px-4 rounded-md border border-zinc-300 text-zinc-600 text-sm font-semibold">
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
      {checkinCouponRes && (
        <CheckinCouponModal
          res={checkinCouponRes}
          onClose={() => setCheckinCouponRes(null)}
          onApplied={load}
        />
      )}
      {showNew && (
        <ReservationForm
          title="Nuova prenotazione"
          submitLabel="Crea prenotazione"
          testidPrefix="new-res"
          rooms={rooms}
          initial={{
            customer: { first_name: "", last_name: "", phone: "", email: "" },
            date, time: "20:00", party_size: 2, room_id: null, table_id: null,
            customer_notes: "", internal_notes: "", etichetta: "", channel: "telefono",
            status: "confermata", notify_customer: true,
          }}
          onSubmit={createReservation}
          onClose={() => setShowNew(false)}
        />
      )}
      {review && (
        <ReservationForm
          title={`Verifica richiesta — ${review.customer_name}`}
          submitLabel="Salva e conferma"
          testidPrefix="review-res"
          rooms={rooms}
          initial={{
            status_choice: "confermata",
            customer: {
              first_name: review.customer_first_name || "",
              last_name: review.customer_last_name || "",
              phone: review.customer_phone || "",
              email: review.customer_email || "",
            },
            date: review.date, time: review.time, party_size: review.party_size,
            room_id: review.room_id || null, table_id: review.table_id || null,
            customer_notes: review.customer_notes || "", internal_notes: review.internal_notes || "",
            etichetta: review.etichetta || "",
            channel: review.channel || "telefono",
          }}
          onSubmit={reviewSubmit}
          onClose={() => setReview(null)}
        />
      )}
    </div>
  );
}
